import { computed, onMounted, onUnmounted, watch } from "vue";
import { setKioskEmployeeId } from "@wkt/lib/auth-context";
import { router } from "@wkt/router";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { useShellStore } from "@wkt/stores/shell";
import { useCheckinStore } from "@wkt/stores/checkin";
let kioskIntervalId: number | null = null;
let kioskActivityListenersBound = false;

const KIOSK_ACTIVITY_EVENTS = [
  "mousedown",
  "touchstart",
  "keydown",
  "input",
  "focusin",
] as const;

export function useKioskMode() {
  const shellStore = useShellStore();
  const sessionCatalogStore = useSessionCatalogStore();
  const checkinStore = useCheckinStore();

  const isKioskMode = computed(() => shellStore.isKioskSession);

  function stopKioskTimer(): void {
    if (kioskIntervalId !== null) {
      window.clearInterval(kioskIntervalId);
      kioskIntervalId = null;
    }
  }

  function startKioskTimer(): void {
    if (typeof window === "undefined" || kioskIntervalId !== null) {
      return;
    }

    shellStore.resetKioskTimeout();
    kioskIntervalId = window.setInterval(() => {
      if (!shellStore.isKioskSession) {
        stopKioskTimer();
        return;
      }

      shellStore.decrementKioskTimeout();
      if (shellStore.kioskTimeout <= 0) {
        stopKioskTimer();
        exitKiosk();
      }
    }, 1000);
  }

  function resetKioskTimeout(): void {
    if (!shellStore.isKioskSession) {
      return;
    }

    shellStore.resetKioskTimeout();
  }

  function bindKioskActivityListeners(): void {
    if (typeof window === "undefined" || kioskActivityListenersBound) {
      return;
    }

    KIOSK_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetKioskTimeout, true);
    });

    kioskActivityListenersBound = true;
  }

  function unbindKioskActivityListeners(): void {
    if (typeof window === "undefined" || !kioskActivityListenersBound) {
      return;
    }

    KIOSK_ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, resetKioskTimeout, true);
    });

    kioskActivityListenersBound = false;
  }

  function syncKioskRuntime(): void {
    shellStore.syncKioskFromStorage();

    if (shellStore.isKioskSession) {
      bindKioskActivityListeners();
      startKioskTimer();
      return;
    }

    unbindKioskActivityListeners();
    stopKioskTimer();
  }

  function exitKiosk(): void {
    stopKioskTimer();
    unbindKioskActivityListeners();
    shellStore.setActiveKioskEmployee(null);
    setKioskEmployeeId(null);
    sessionCatalogStore.clearSession({ preserveClientCompany: true });
    checkinStore.$reset();
    void router.replace({ name: "kiosk" }).catch(() => undefined);
  }

  onMounted(() => {
    syncKioskRuntime();
  });

  watch(
    isKioskMode,
    (enabled) => {
      if (enabled) {
        bindKioskActivityListeners();
        startKioskTimer();
        return;
      }

      unbindKioskActivityListeners();
      stopKioskTimer();
    },
    { flush: "post" },
  );

  onUnmounted(() => {
    unbindKioskActivityListeners();
    stopKioskTimer();
  });

  return {
    isKioskMode,
    resetKioskTimeout,
    startKioskTimer,
    stopKioskTimer,
    syncKioskRuntime,
    exitKiosk,
  };
}
